import GlobalIcon from "../GlobalIcon/GlobalIcon";
import {
  bookMetaPnlTone,
  formatBookMetaCount,
  formatBookMetaPnl,
} from "../../utils/bookMetaSummary";
import styles from "./BookMetaCards.module.css";

export const HOLDING_BOOK_META_CARDS = [
  {
    key: "totalAccounts",
    label: "Total Accounts",
    icon: "PeopleIcon",
    valueType: "count",
    iconTone: "accent",
  },
  {
    key: "invested",
    label: "Invested",
    icon: "FundsIcon",
    valueType: "currency",
    iconTone: "neutral",
  },
  {
    key: "currentValue",
    label: "Current Value",
    icon: "DocumentIcon",
    valueType: "currency",
    iconTone: "neutral",
  },
  {
    key: "pnl",
    label: "Total PnL",
    icon: "UpDownArrowIcon",
    valueType: "pnl",
    iconTone: "pnl",
    emphasized: true,
    subKey: "pnlPercentage",
    subValueType: "pct",
  },
];

/** Default cards for position book — reuse on other book pages with the same API meta shape. */
export const POSITION_BOOK_META_CARDS = [
  {
    key: "totalAccounts",
    label: "Total Accounts",
    icon: "PeopleIcon",
    valueType: "count",
    iconTone: "accent",
  },
  {
    key: "realisedPnl",
    label: "Realised PnL",
    icon: "VerifiedIcon",
    valueType: "pnl",
    iconTone: "pnl",
  },
  {
    key: "unrealisedPnl",
    label: "Unrealised PnL",
    icon: "UpDownArrowIcon",
    valueType: "pnl",
    iconTone: "pnl",
  },
  {
    key: "totalPnl",
    label: "Total PnL",
    icon: "FundsIcon",
    valueType: "pnl",
    iconTone: "pnl",
    emphasized: true,
  },
];

function formatCardValue(card, summary, loading) {
  if (loading) return "—";
  if (!summary) return "—";

  const raw = summary[card.key];
  if (card.valueType === "pnl") return formatBookMetaPnl(raw);
  if (card.valueType === "currency") return formatBookMetaPnl(Math.abs(Number(raw)) * (raw < 0 ? -1 : 1));
  return formatBookMetaCount(raw);
}

function formatSubValue(card, summary) {
  if (!card.subKey || !summary) return null;
  const raw = summary[card.subKey];
  if (raw == null || Number.isNaN(Number(raw))) return null;
  const n = Number(raw);
  const formatted = Math.abs(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const prefix = n > 0 ? "+" : n < 0 ? "-" : "";
  return `${prefix}${formatted}%`;
}

function valueToneClass(card, summary) {
  if (card.valueType !== "pnl") return "";
  const tone = bookMetaPnlTone(summary?.[card.key]);
  if (tone === "positive") return styles.valuePositive;
  if (tone === "negative") return styles.valueNegative;
  return styles.valueNeutral;
}

function iconWrapClass(card, summary) {
  if (card.iconTone === "accent") return styles.iconWrapAccent;
  if (card.iconTone === "pnl" && card.valueType === "pnl") {
    const tone = bookMetaPnlTone(summary?.[card.key]);
    if (tone === "positive") return styles.iconWrapPositive;
    if (tone === "negative") return styles.iconWrapNegative;
  }
  return styles.iconWrapNeutral;
}

function cardSurfaceClass(card, summary) {
  const classes = [styles.card];
  if (card.emphasized) classes.push(styles.cardEmphasized);

  if (card.valueType === "pnl") {
    const tone = bookMetaPnlTone(summary?.[card.key]);
    if (tone === "positive") classes.push(styles.cardPositive);
    else if (tone === "negative") classes.push(styles.cardNegative);
  }

  return classes.join(" ");
}

function BookMetaCards({
  summary,
  cards = POSITION_BOOK_META_CARDS,
  loading = false,
  className = "",
  endSlot = null,
}) {
  const showCards = loading || Boolean(summary);
  if (!showCards && !endSlot) return null;

  return (
    <div className={`${styles.metaBand} ${className}`.trim()}>
      {showCards ? (
        <div
          className={`${styles.cardsRow}${endSlot ? ` ${styles.cardsRowWithEndSlot}` : ""}`}
          role="group"
          aria-label="Book summary"
        >
          {cards.map((card) => (
            <article key={card.key} className={cardSurfaceClass(card, summary)}>
              <div className={`${styles.iconWrap} ${iconWrapClass(card, summary)}`} aria-hidden="true">
                <GlobalIcon iconName={card.icon} className={styles.icon} />
              </div>
              <div className={styles.body}>
                <p className={styles.label}>{card.label}</p>
                <div className={styles.valueRow}>
                  <p className={`${styles.value} ${valueToneClass(card, summary)}`}>
                    {formatCardValue(card, summary, loading)}
                  </p>
                  {!loading && formatSubValue(card, summary) && (
                    <span className={`${styles.subValue} ${valueToneClass(card, summary)}`}>
                      {formatSubValue(card, summary)}
                    </span>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : null}
      {endSlot ? <div className={styles.endSlot}>{endSlot}</div> : null}
    </div>
  );
}

export default BookMetaCards;
